import { ENVIRONMENTS, normalizeHostname, normalizeOrigin, type TraceEnvironment } from '@traceio-org/platform-contracts';

/** Audience of platform-api tokens when VITE_AUTH0_AUDIENCE is not set (development identity). */
export const DEFAULT_PLATFORM_AUDIENCE = 'urn:trace:api:platform';
const LOCAL_API_URL = 'http://localhost:4720';
const LOCAL_HUB_ORIGIN = 'http://localhost:4710';

export interface Auth0Settings {
  domain: string;
  clientId: string;
  audience: string;
}

export interface HubConfig {
  /** platform-api base URL (origin plus optional path, no trailing slash). */
  platformApiUrl: string;
  environment: TraceEnvironment;
  /** Null means the identity provider is not configured: no SSO buttons, only development identity. */
  auth0: Auth0Settings | null;
  /** Audience the hub requests platform-api tokens for. */
  platformAudience: string;
  /** Canonical Trace sign-in origin, linked from unknown hosts; null when unknown. */
  hubOrigin: string | null;
  /** Development identity can only ever be used in development and test builds. */
  devIdentityAllowed: boolean;
}

export type ConfigResult =
  | { ok: true; config: HubConfig; warnings: string[] }
  | { ok: false; errors: string[] };

type Env = Record<string, string | boolean | undefined>;

function read(env: Env, name: string): string | undefined {
  const value = env[name];
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

function parseBaseUrl(value: string): string | null {
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.username || url.password || url.search || url.hash) return null;
    return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/**
 * Validates the hub's build-time environment once at startup. Only the dev server (`vite`) may fall
 * back to local defaults; a production build with a missing or invalid value renders a configuration
 * error page instead of guessing. Auth0 needs both the domain and the client id: a partial
 * configuration counts as not configured.
 */
export function loadHubConfig(env: Env, options: { dev: boolean }): ConfigResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  let environment: TraceEnvironment | undefined;
  const rawEnvironment = read(env, 'VITE_TRACE_ENV');
  if (rawEnvironment === undefined) {
    if (options.dev) {
      environment = 'development';
      warnings.push('VITE_TRACE_ENV is not set; using development.');
    } else {
      errors.push('VITE_TRACE_ENV is required (development, test, staging or production).');
    }
  } else if ((ENVIRONMENTS as readonly string[]).includes(rawEnvironment)) {
    environment = rawEnvironment as TraceEnvironment;
  } else {
    errors.push('VITE_TRACE_ENV must be development, test, staging or production.');
  }
  const deployed = environment === 'staging' || environment === 'production';

  let platformApiUrl: string | undefined;
  const rawApiUrl = read(env, 'VITE_PLATFORM_API_URL');
  if (rawApiUrl === undefined) {
    if (options.dev) {
      platformApiUrl = LOCAL_API_URL;
      warnings.push(`VITE_PLATFORM_API_URL is not set; using ${LOCAL_API_URL}.`);
    } else {
      errors.push('VITE_PLATFORM_API_URL is required.');
    }
  } else {
    const parsed = parseBaseUrl(rawApiUrl);
    if (!parsed) errors.push('VITE_PLATFORM_API_URL must be an http or https URL without a query or fragment.');
    else if (deployed && !parsed.startsWith('https:')) errors.push('VITE_PLATFORM_API_URL must use https in staging and production.');
    else platformApiUrl = parsed;
  }

  // The audience doubles as the platform-api audience for development identity tokens, so it may be set
  // alone. Domain and client id together switch the identity provider on.
  let auth0: Auth0Settings | null = null;
  const domain = read(env, 'VITE_AUTH0_DOMAIN');
  const clientId = read(env, 'VITE_AUTH0_CLIENT_ID');
  const audience = read(env, 'VITE_AUTH0_AUDIENCE') ?? DEFAULT_PLATFORM_AUDIENCE;
  if (domain && clientId) {
    // The SDK expects a bare host (tenant or custom domain), never a URL.
    if (!normalizeHostname(domain).ok) errors.push('VITE_AUTH0_DOMAIN must be a host name such as login.trace.io.');
    else if (!/^[A-Za-z0-9_-]{1,128}$/.test(clientId)) errors.push('VITE_AUTH0_CLIENT_ID has unexpected characters.');
    else auth0 = { domain, clientId, audience };
  } else if (domain || clientId) {
    const missing = domain ? 'VITE_AUTH0_CLIENT_ID' : 'VITE_AUTH0_DOMAIN';
    warnings.push(`Auth0 is only partly configured (missing ${missing}); sign-in stays not configured.`);
  }

  let hubOrigin: string | null = null;
  const rawHubOrigin = read(env, 'VITE_HUB_ORIGIN');
  if (rawHubOrigin !== undefined) {
    try {
      hubOrigin = normalizeOrigin(rawHubOrigin);
    } catch {
      errors.push('VITE_HUB_ORIGIN must be an origin such as https://accounts.trace.io.');
    }
  } else if (environment === 'development' || environment === 'test') {
    hubOrigin = LOCAL_HUB_ORIGIN;
  }

  if (errors.length > 0 || !environment || !platformApiUrl) return { ok: false, errors };
  return {
    ok: true,
    warnings,
    config: {
      platformApiUrl,
      environment,
      auth0,
      platformAudience: audience,
      hubOrigin,
      devIdentityAllowed: environment === 'development' || environment === 'test',
    },
  };
}
